import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  IDTProductContext,
  IDTProjectDetail,
  IDTStimuli,
  ISimulationRun,
} from "../models/interfaces";

interface DTProjectDetailState {
  project: IDTProjectDetail | null;
  selectedPersonaIds: string[];
  stimuli: IDTStimuli[];
  productContexts: IDTProductContext[];
  isLoading: boolean;
  isStimuliLoading: boolean;
  isStimuliModalOpen: boolean;
  selectedStimuliId: string | null;
  simulationRuns: ISimulationRun[];
  searchText: string;
  specialtyFilter: string | null;
  selectedRunId: string | null;
}

export const initialState: DTProjectDetailState = {
  project: null,
  selectedPersonaIds: [],
  stimuli: [],
  productContexts: [],
  isLoading: false,
  isStimuliLoading: false,
  isStimuliModalOpen: false,
  selectedStimuliId: null,
  simulationRuns: [],
  searchText: "",
  specialtyFilter: null,
  selectedRunId: null,
};

const dtProjectDetailSlice = createSlice({
  name: "dtProjectDetailSlice",
  initialState,
  reducers: {
    setProject: (state, action: PayloadAction<IDTProjectDetail>) => {
      state.project = action.payload;
    },
    setSelectedPersonaIds: (state, action: PayloadAction<string[]>) => {
      state.selectedPersonaIds = action.payload;
    },
    togglePersonaSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const idx = state.selectedPersonaIds.indexOf(id);
      if (idx >= 0) {
        state.selectedPersonaIds.splice(idx, 1);
      } else {
        state.selectedPersonaIds.push(id);
      }
    },
    selectAllPersonas: (state) => {
      state.selectedPersonaIds =
        state.project?.dt_personas?.map((p) => p.persona_id) ?? [];
    },
    deselectAllPersonas: (state) => {
      state.selectedPersonaIds = [];
    },
    setStimuli: (state, action: PayloadAction<IDTStimuli[]>) => {
      state.stimuli = action.payload;
    },
    addStimuli: (state, action: PayloadAction<IDTStimuli>) => {
      state.stimuli.unshift(action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStimuliLoading: (state, action: PayloadAction<boolean>) => {
      state.isStimuliLoading = action.payload;
    },
    setStimuliModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isStimuliModalOpen = action.payload;
    },
    setSelectedStimuliId: (state, action: PayloadAction<string | null>) => {
      state.selectedStimuliId = action.payload;
    },
    updateStimuliInList: (state, action: PayloadAction<IDTStimuli>) => {
      const idx = state.stimuli.findIndex((s) => s.id === action.payload.id);
      if (idx >= 0) {
        state.stimuli[idx] = { ...state.stimuli[idx], ...action.payload };
      }
    },
    removeStimuliFromList: (state, action: PayloadAction<string>) => {
      state.stimuli = state.stimuli.filter((s) => s.id !== action.payload);
    },
    setProductContexts: (state, action: PayloadAction<IDTProductContext[]>) => {
      state.productContexts = action.payload;
    },
    upsertProductContext: (state, action: PayloadAction<IDTProductContext>) => {
      const idx = state.productContexts.findIndex((item) => item.id === action.payload.id);
      if (idx >= 0) {
        state.productContexts[idx] = { ...state.productContexts[idx], ...action.payload };
      } else {
        state.productContexts.unshift(action.payload);
      }
    },
    removeProductContextFromList: (state, action: PayloadAction<string>) => {
      state.productContexts = state.productContexts.filter((item) => item.id !== action.payload);
    },
    setSimulationRuns: (state, action: PayloadAction<ISimulationRun[]>) => {
      state.simulationRuns = action.payload;
    },
    addSimulationRun: (state, action: PayloadAction<ISimulationRun>) => {
      state.simulationRuns.unshift(action.payload);
    },
    removeSimulationRun: (state, action: PayloadAction<string>) => {
      state.simulationRuns = state.simulationRuns.filter((r) => r.id !== action.payload);
    },
    updateSimulationRun: (state, action: PayloadAction<ISimulationRun>) => {
      const idx = state.simulationRuns.findIndex((r) => r.id === action.payload.id);
      if (idx >= 0) {
        state.simulationRuns[idx] = action.payload;
      }
    },
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    setSpecialtyFilter: (state, action: PayloadAction<string | null>) => {
      state.specialtyFilter = action.payload;
    },
    setSelectedRunId: (state, action: PayloadAction<string | null>) => {
      state.selectedRunId = action.payload;
    },
    resetDTProjectDetail: () => initialState,
  },
});

export const {
  addSimulationRun,
  addStimuli,
  deselectAllPersonas,
  removeProductContextFromList,
  removeSimulationRun,
  removeStimuliFromList,
  resetDTProjectDetail,
  selectAllPersonas,
  setLoading,
  setProductContexts,
  setProject,
  setSearchText,
  setSelectedPersonaIds,
  setSelectedRunId,
  setSelectedStimuliId,
  setSimulationRuns,
  setSpecialtyFilter,
  setStimuli,
  setStimuliLoading,
  setStimuliModalOpen,
  togglePersonaSelection,
  updateSimulationRun,
  updateStimuliInList,
  upsertProductContext,
} = dtProjectDetailSlice.actions;

export default dtProjectDetailSlice.reducer;
